import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchPageBySlug, getResizedImageUrl } from '../lib/cms';
import ContentWithCTA from '../components/ContentWithCTA';

export default function OmOs() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPage() {
      const data = await fetchPageBySlug('om-larsen-kristensen');
      setPageData(data);
      setLoading(false);
    }
    loadPage();
  }, []);

  useEffect(() => {
    if (pageData) {
      document.title = pageData.meta_title || 'Om Larsen & Kristensen - Larsen & Kristensen';
      // Set meta description
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', pageData.meta_description || '');
      }
    }
  }, [pageData]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#163A2B] border-t-transparent"></div>
      </div>
    );
  }

  const heroBg = pageData?.hero_image?.[0] || 'https://assets.auxodev.dk/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/sommerhus-0_3.jpeg';
  const resizedHeroBg = getResizedImageUrl(heroBg, 1600, 80);

  const values = [
    {
      title: 'Solidt Håndværk',
      icon: 'fa-solid fa-trowel-bricks',
      desc: 'Vi går aldrig på kompromis med kvaliteten. Vores arbejde bygger på stolte håndværksmæssige traditioner og præcision.'
    },
    {
      title: 'Pålidelighed',
      icon: 'fa-solid fa-handshake',
      desc: 'En aftale er en aftale. Vi leverer til tiden, overholder budgettet og holder dig løbende orienteret under hele processen.'
    },
    {
      title: 'Lokal Forankring',
      icon: 'fa-solid fa-house-chimney',
      desc: 'Med base i Skive kender vi lokalområdet og sætter en ære i at bidrage positivt til udviklingen i Midt- og Vestjylland.'
    },
    {
      title: 'Totalløsninger',
      icon: 'fa-solid fa-arrows-spin',
      desc: 'Vi koordinerer hele dit projekt fra start til slut, så du kun skal henvende dig ét sted og får en problemfri proces.'
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      
      {/* Page Header / Hero */}
      <section className="relative bg-[#1C1E1D] py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={resizedHeroBg}
            alt="Om Larsen & Kristensen"
            className="h-full w-full object-cover object-center opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#163A2B]/50 via-[#1C1E1D]/70 to-[#1C1E1D]/90"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#163A2B] bg-[#F4F1EA] px-3 py-1.5 rounded-full">
            Historien bag
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4 font-display">
            {pageData?.title || 'Om Larsen & Kristensen'}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[#F4F1EA]/80 max-w-2xl mx-auto leading-relaxed">
            {pageData?.intro || 'Historien om to lokale virksomheder, der forenede deres kompetencer for at skabe en stærkere samarbejdspartner.'}
          </p>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Column: Detailed Story */}
            <div className="lg:col-span-7 space-y-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1C1E1D] font-display">
                To lokale fagmænd forener kræfterne
              </h2>
              <ContentWithCTA 
                html={pageData?.content_html} 
                className="text-base text-[#1C1E1D]/80 leading-relaxed space-y-6"
                currentSlug="om-larsen-kristensen"
              />
              <div className="pt-6 border-t border-[#E2E2E0] flex flex-col sm:flex-row gap-6">
                <div>
                  <p className="text-xs text-[#1C1E1D]/50 uppercase font-bold">Anlægsfirmaet</p>
                  <p className="text-lg font-bold text-[#163A2B] font-display">K. Larsen</p>
                  <p className="text-sm text-[#1C1E1D]/70 mt-1">Eksperter i flisebelægning, jordarbejde, haveanlæg og entreprenøropgaver.</p>
                </div>
                <div className="border-l border-[#E2E2E0] pl-6 hidden sm:block"></div>
                <div>
                  <p className="text-xs text-[#1C1E1D]/50 uppercase font-bold">Byggeri & Bolig</p>
                  <p className="text-lg font-bold text-[#1C1E1D] font-display">LHK byg og bolig</p>
                  <p className="text-sm text-[#1C1E1D]/70 mt-1">Specialister i murerarbejde, betonstøbning og renovering.</p>
                </div>
              </div>
            </div>

            {/* Right Column: Visual Callout & Stats */}
            <div className="lg:col-span-5 space-y-8">
              <div className="aspect-4/3 overflow-hidden rounded-lg border border-[#E2E2E0] shadow-sm">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/54439585-0ea9-462f-a6c1-e69db40e10d7/l-k.jpg"
                  alt="Larsen & Kristensen"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Info Box - NO hover effects as it is not clickable */}
              <div className="bg-[#F4F1EA] border border-[#E2E2E0] rounded-lg p-6 sm:p-8">
                <h3 className="text-lg font-bold text-[#1C1E1D] mb-3 font-display">Hvorfor vælge os?</h3>
                <p className="text-sm text-[#1C1E1D]/80 leading-relaxed mb-4">
                  Når du samarbejder med Larsen & Kristensen, får du det bedste fra to verdener: Anlægsfirmaet K. Larsens store maskinpark og ekspertise inden for terræn og belægning, kombineret med LHK byg og boligs solide murer- og betonhåndværk.
                </p>
                <p className="text-sm text-[#1C1E1D]/80 leading-relaxed">
                  Det betyder færre bekymringer for dig, en mere effektiv byggeproces og et færdigt resultat, hvor alle detaljer hænger sammen.
                </p>
              </div>

              {/* Lille note om samarbejdspartnere */}
              <div className="bg-[#163A2B]/5 border border-[#163A2B]/15 rounded-lg p-5">
                <h4 className="text-xs font-bold uppercase tracking-wider text-[#163A2B] mb-2 flex items-center gap-2">
                  <i className="fa-solid fa-handshake text-sm" />
                  Samarbejdspartnere & totalløsninger
                </h4>
                <p className="text-xs text-[#1C1E1D]/80 leading-relaxed">
                  Selvom vores egne spidskompetencer ligger inden for murerarbejde, betonstøbning, jordarbejde og anlæg, samarbejder vi tæt med et fast netværk af dygtige, lokale tømrere, elektrikere og VVS-installatører for at sikre dig en komplet totalløsning.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 sm:py-24 bg-[#F4F1EA]/50 border-t border-[#E2E2E0]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#163A2B] bg-[#F4F1EA] px-3 py-1.5 rounded-full">
              Vores Fundament
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1C1E1D] mt-4 font-display">
              Værdier vi bygger på
            </h2>
            <p className="text-base text-[#1C1E1D]/70 mt-3">
              Vores daglige arbejde styres af fire kerneværdier, der sikrer, at du altid får en professionel og tryg oplevelse.
            </p>
          </div>

          {/* Values Grid - NO hover effects on cards as they are not clickable */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((val, idx) => (
              <div key={idx} className="bg-white border border-[#E2E2E0] rounded-lg p-6 sm:p-8 shadow-sm flex flex-col items-center text-center">
                <div className="inline-flex items-center justify-center rounded-full bg-[#F4F1EA] text-[#163A2B] w-14 h-14 mb-6">
                  <i className={`${val.icon} text-2xl`} />
                </div>
                <h3 className="text-lg font-bold text-[#1C1E1D] font-display">{val.title}</h3>
                <p className="text-sm text-[#1C1E1D]/70 mt-3 leading-relaxed">{val.desc}</p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1C1E1D] py-16 text-center border-t border-[#E2E2E0]">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white font-display">
            Skal vi hjælpe dig med dit næste projekt?
          </h2>
          <p className="text-sm sm:text-base text-[#F4F1EA]/80 mt-3 max-w-xl mx-auto">
            Vi står altid klar til at tage en uforpligtende snak om dine drømme og planer. Ring til os eller udfyld formularen.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center rounded-md bg-[#163A2B] px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-white hover:text-[#1C1E1D] transition-colors cursor-pointer"
            >
              Få et uforpligtende tilbud
            </Link>
            <a
              href="tel:+4530713311"
              className="inline-flex items-center justify-center rounded-md border border-white/40 bg-transparent px-6 py-3 text-sm font-semibold text-white hover:bg-white hover:text-[#1C1E1D] hover:border-white transition-colors cursor-pointer"
            >
              Ring <span className="whitespace-nowrap ml-1">30 71 33 11</span>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
